import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const MARGEN     = 15;
const ANCHO_UTIL = 210 - MARGEN * 2;
const ALTO_PAGINA = 297;
const ALTO_UTIL  = ALTO_PAGINA - MARGEN * 2;

function formatearFecha(f) {
  if (!f) return '';
  const [y, m, d] = f.split('-');
  return `${d}/${m}/${y}`;
}

function fechaHoy() {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

async function cargarImagen(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function agregarEncabezado(doc, logo, nombrePaciente) {
  const y = MARGEN;
  if (logo) {
    const ratio = logo.width / logo.height;
    const h = 12; const w = h * ratio;
    doc.addImage(logo, 'PNG', MARGEN, y - 4, w, h);
  }
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(44, 62, 80);
  doc.text(`Ficha de ${nombrePaciente}`, logo ? MARGEN + 50 : MARGEN, y + 4);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(127, 140, 141);
  doc.text(`Generado: ${fechaHoy()}`, 210 - MARGEN, y + 4, { align: 'right' });
  doc.setDrawColor(189, 195, 199);
  doc.setLineWidth(0.5);
  doc.line(MARGEN, y + 10, 210 - MARGEN, y + 10);
  return y + 16;
}

function agregarTituloSeccion(doc, titulo, y) {
  if (y + 14 > ALTO_PAGINA - MARGEN) { doc.addPage(); y = MARGEN; }
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(44, 62, 80);
  doc.text(titulo, MARGEN, y);
  doc.setDrawColor(52, 152, 219);
  doc.setLineWidth(0.8);
  doc.line(MARGEN, y + 2, MARGEN + doc.getTextWidth(titulo), y + 2);
  return y + 10;
}

function agregarCampo(doc, label, valor, y) {
  if (y + 10 > ALTO_PAGINA - MARGEN) { doc.addPage(); y = MARGEN; }
  const valorTexto = String(valor || '-');
  const INDENT = 5;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(52, 73, 94);

  const labelConColon = `${label}: `;
  const labelWidth = doc.getTextWidth(labelConColon);

  // Si el valor cabe en la misma línea que la etiqueta, renderizar en una sola línea
  const anchoRestante = ANCHO_UTIL - labelWidth;
  if (anchoRestante > 30 && doc.getTextWidth(valorTexto) <= anchoRestante) {
    doc.text(labelConColon, MARGEN, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(44, 62, 80);
    doc.text(valorTexto, MARGEN + labelWidth, y);
    return y + 7;
  }

  // Etiqueta en su propia línea, valor debajo con sangría
  doc.text(labelConColon, MARGEN, y);
  y += 5;

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(44, 62, 80);
  const lineas = doc.splitTextToSize(valorTexto, ANCHO_UTIL - INDENT);
  for (const linea of lineas) {
    if (y + 5 > ALTO_PAGINA - MARGEN) { doc.addPage(); y = MARGEN; }
    doc.text(linea, MARGEN + INDENT, y);
    y += 5;
  }
  return y + 3;
}

function agregarTextoLibre(doc, texto, y) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(44, 62, 80);
  const lineas = doc.splitTextToSize(texto || '', ANCHO_UTIL);
  for (const linea of lineas) {
    if (y + 5 > ALTO_PAGINA - MARGEN) { doc.addPage(); y = MARGEN; }
    doc.text(linea, MARGEN, y);
    y += 5;
  }
  return y;
}

async function capturarNodoDOM(nodo) {
  const canvas = await html2canvas(nodo, {
    scale: 2, useCORS: true, allowTaint: true, logging: false, backgroundColor: '#ffffff',
  });
  return canvas.toDataURL('image/png');
}

function agregarImagenMultipagina(doc, imgData, imgWidth, imgHeight, startY) {
  let y = startY;
  const pdfWidth = ANCHO_UTIL;
  const pdfHeight = (imgHeight * pdfWidth) / imgWidth;
  const altoDisponible = ALTO_PAGINA - MARGEN - y;

  if (pdfHeight <= altoDisponible) {
    doc.addImage(imgData, 'PNG', MARGEN, y, pdfWidth, pdfHeight);
    return Promise.resolve(y + pdfHeight + 5);
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const img = new Image();

  return new Promise((resolve) => {
    img.onload = () => {
      let offsetY = 0;
      const escala = imgWidth / pdfWidth;
      let currentY = y;
      while (offsetY < imgHeight) {
        const altoDisp = currentY === y ? altoDisponible : ALTO_UTIL;
        const altoFragmento = Math.min(altoDisp * escala, imgHeight - offsetY);
        canvas.width = imgWidth; canvas.height = altoFragmento;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, offsetY, imgWidth, altoFragmento, 0, 0, imgWidth, altoFragmento);
        const fragmento = canvas.toDataURL('image/png');
        const altoPDF = (altoFragmento * pdfWidth) / imgWidth;
        if (offsetY > 0) { doc.addPage(); currentY = MARGEN; }
        doc.addImage(fragmento, 'PNG', MARGEN, currentY, pdfWidth, altoPDF);
        offsetY += altoFragmento;
        currentY = currentY + altoPDF;
      }
      resolve(currentY + 5);
    };
    img.src = imgData;
  });
}

function agregarNumerosPagina(doc) {
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(149, 165, 166);
    doc.text(`${i} / ${total}`, 105, ALTO_PAGINA - 8, { align: 'center' });
  }
}

async function agregarFirma(doc, firmaImg, y) {
  if (!firmaImg) return y;
  if (y + 45 > ALTO_PAGINA - MARGEN) { doc.addPage(); y = MARGEN + 10; }

  // Renderizar la firma en canvas para obtener dataURL (necesario para CORS)
  const canvas = document.createElement('canvas');
  canvas.width = firmaImg.width; canvas.height = firmaImg.height;
  canvas.getContext('2d').drawImage(firmaImg, 0, 0);
  const imgData = canvas.toDataURL('image/png');

  const ratio = firmaImg.width / firmaImg.height;
  const h = 22; const w = Math.min(h * ratio, 65);
  doc.addImage(imgData, 'PNG', MARGEN, y, w, h);

  // Línea bajo la firma
  doc.setDrawColor(120, 120, 120);
  doc.setLineWidth(0.3);
  doc.line(MARGEN, y + h + 3, MARGEN + 70, y + h + 3);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(127, 140, 141);
  doc.text('Firma del profesional', MARGEN, y + h + 8);

  return y + h + 14;
}

const GMFCS_VERSION_LABEL = { children: 'Niños', adults: 'Adultos / Más grandes' };

export default async function generarPDF({
  nombre,
  campos,
  sesiones,
  secciones,
  isakSecciones   = [], // [{ titulo, fecha, nodo }]
  firmaUrl        = null,
  firmaTexto      = null,
  recommendations = null,
  kinesioData     = null,
  retornarBlob    = false,
}) {
  const doc      = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const logo     = await cargarImagen('/LogoSoftCraft.png');
  const firmaImg = firmaUrl ? await cargarImagen(firmaUrl) : null;

  let y = agregarEncabezado(doc, logo, nombre);

  // ── Datos personales ──
  if (secciones.datosPersonales && campos.length > 0) {
    y = agregarTituloSeccion(doc, 'Datos Personales', y);
    for (const campo of campos) {
      if (campo.value) y = agregarCampo(doc, campo.label || 'Sin título', campo.value, y);
    }
    y += 4;
  }

  // ── Fichas ISAK (puede haber múltiples) ──
  for (const isak of isakSecciones) {
    if (!isak.nodo) continue;
    const titulo = isak.titulo || 'Evaluación Antropométrica ISAK';
    const subtitulo = isak.fecha ? `${titulo} — ${isak.fecha}` : titulo;
    y = agregarTituloSeccion(doc, subtitulo, y);
    try {
      const imgData = await capturarNodoDOM(isak.nodo);
      const img = await cargarImagen(imgData);
      if (img) y = await agregarImagenMultipagina(doc, imgData, img.width, img.height, y);
    } catch (err) {
      console.error('Error capturando ISAK:', err);
      y = agregarCampo(doc, 'Error', 'No se pudo capturar esta evaluación ISAK', y);
    }
    y += 4;
  }

  // ── Historial de sesiones ──
  if (secciones.historialSesiones && sesiones.length > 0) {
    if (y + 20 > ALTO_PAGINA - MARGEN) { doc.addPage(); y = MARGEN; }
    y = agregarTituloSeccion(doc, 'Historial de Sesiones', y);
    for (const sesion of sesiones) {
      if (y + 16 > ALTO_PAGINA - MARGEN) { doc.addPage(); y = MARGEN; }
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(44, 62, 80);
      doc.text(`Sesión del ${formatearFecha(sesion.date)}`, MARGEN, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(44, 62, 80);
      doc.setFontSize(9);
      const lineas = doc.splitTextToSize(sesion.notes || '', ANCHO_UTIL - 5);
      for (const linea of lineas) {
        if (y + 5 > ALTO_PAGINA - MARGEN) { doc.addPage(); y = MARGEN; }
        doc.text(linea, MARGEN + 5, y);
        y += 4.5;
      }
      y += 4;
    }
  }

  // ── Kinesiología ──
  if (secciones.kinesio && kinesioData) {
    if (y + 20 > ALTO_PAGINA - MARGEN) { doc.addPage(); y = MARGEN; }
    y = agregarTituloSeccion(doc, 'Kinesiología', y);

    if (kinesioData.area) {
      const areaLabel = { motor: 'Área Motora', respiratory: 'Área Respiratoria', both: 'Ambas' };
      y = agregarCampo(doc, 'Área', areaLabel[kinesioData.area] || kinesioData.area, y);
    }
    if (kinesioData.firstSessionDate) {
      y = agregarCampo(doc, 'Primera sesión', formatearFecha(kinesioData.firstSessionDate), y);
    }

    const gmfcsParaExportar = (kinesioData.gmfcsRecords || []).filter(r => !kinesioData.gmfcsIds || kinesioData.gmfcsIds.includes(r.id));
    for (const g of gmfcsParaExportar) {
      y += 2;
      const fechaEval = g.date ? formatearFecha(g.date) : '';
      const tituloGMFCS = fechaEval ? `Evaluación GMFCS — ${fechaEval}` : 'Evaluación GMFCS';
      y = agregarTituloSeccion(doc, tituloGMFCS, y);
      y = agregarCampo(doc, 'Versión', GMFCS_VERSION_LABEL[g.version] || g.version, y);
      if (g.level) y = agregarCampo(doc, 'Nivel', `Nivel ${g.level}`, y);
      if (g.notes) y = agregarCampo(doc, 'Observaciones', g.notes, y);
    }

    const threshParaExportar = (kinesioData.threshRecords || []).filter(r => !kinesioData.threshIds || kinesioData.threshIds.includes(r.id));
    for (const t of threshParaExportar) {
      if (t.imt_max == null && t.pep_max == null) continue;
      y += 2;
      const fechaThresh = t.date ? formatearFecha(t.date) : '';
      const tituloThresh = fechaThresh ? `Threshold (cmH2O) — ${fechaThresh}` : 'Threshold (cmH2O)';
      y = agregarTituloSeccion(doc, tituloThresh, y);
      if (t.imt_max != null) {
        y = agregarCampo(doc, 'IMT máximo', `${t.imt_max} cmH2O`, y);
        y = agregarCampo(doc, 'IMT trabajo 30%', `${(parseFloat(t.imt_max) * 0.3).toFixed(1)} cmH2O`, y);
      }
      if (t.pep_max != null) {
        y = agregarCampo(doc, 'PEP máximo', `${t.pep_max} cmH2O`, y);
        y = agregarCampo(doc, 'PEP trabajo 30%', `${(parseFloat(t.pep_max) * 0.3).toFixed(1)} cmH2O`, y);
      }
    }
    y += 4;
  }

  // ── Recomendaciones ──
  // Acepta array de registros { text, date } (nuevo) o string plano (compat.)
  if (secciones.recomendaciones && recommendations) {
    if (y + 20 > ALTO_PAGINA - MARGEN) { doc.addPage(); y = MARGEN; }
    y = agregarTituloSeccion(doc, 'Recomendaciones / Sugerencias', y);
    if (Array.isArray(recommendations)) {
      for (const rec of recommendations) {
        if (y + 20 > ALTO_PAGINA - MARGEN) { doc.addPage(); y = MARGEN; }
        if (rec.date) {
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(44, 62, 80);
          doc.text(formatearFecha(rec.date), MARGEN, y);
          y += 5;
        }
        y = agregarTextoLibre(doc, rec.text, y);
        y += 4;
      }
    } else {
      y = agregarTextoLibre(doc, recommendations, y);
    }
    y += 6;
  }

  // ── Firmas (dibujada a la izquierda, texto a la derecha) ──
  if (firmaImg || firmaTexto) {
    if (y + 45 > ALTO_PAGINA - MARGEN) { doc.addPage(); y = MARGEN + 10; }
    const yFirma = y + 8;

    if (firmaImg) {
      y = await agregarFirma(doc, firmaImg, yFirma);
    }

    if (firmaTexto) {
      const yTexto = yFirma + 10;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(44, 62, 80);
      doc.text(firmaTexto, 210 - MARGEN, yTexto, { align: 'right' });
      doc.setDrawColor(120, 120, 120);
      doc.setLineWidth(0.3);
      doc.line(210 - MARGEN - 70, yTexto + 3, 210 - MARGEN, yTexto + 3);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(127, 140, 141);
      doc.text('Firma del profesional', 210 - MARGEN, yTexto + 8, { align: 'right' });
      y = Math.max(y, yTexto + 14);
    }
  }

  agregarNumerosPagina(doc);

  if (retornarBlob) {
    return doc.output('blob');
  }

  const nombreArchivo = `ficha-${nombre.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(nombreArchivo);
}
